import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@ovComponents/2-component/dialog/dialog';
import { Box, Button, Typography, useTheme } from 'hephaestus/web';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export type SessionTimeOutModalProps = {
  open: boolean;
  onLogout: () => void;
  stayLoggedIn: () => void;
  onSessionTimeOut: () => void;
};

const InactiveLayoutModal = ({
  open,
  onLogout,
  stayLoggedIn,
  onSessionTimeOut,
}: SessionTimeOutModalProps): JSX.Element => {
  const { t } = useTranslation(['shared']);
  const theme = useTheme();
  const [duration, setDuration] = useState(30);
  const ticker = useRef<ReturnType<typeof setInterval> | undefined>();

  const logoutInitiatedRef = useRef(false);

  const startTimer = (): void => {
    logoutInitiatedRef.current = false;
    ticker.current = setInterval(() => setDuration((prev) => prev - 1), 1000);
  };

  const handleClose = (): void => {
    if (ticker.current) clearInterval(ticker.current);
    logoutInitiatedRef.current = false;
    stayLoggedIn();
    setDuration(30);
  };

  const handleLogout = (): void => {
    // Mark that logout was manually initiated - prevents race condition
    logoutInitiatedRef.current = true;
    // Stop the timer immediately - this prevents the countdown from continuing
    if (ticker.current) {
      clearInterval(ticker.current);
      ticker.current = undefined;
    }
    // Now that the timer is stopped, call the logout function
    onLogout();
  };

  useEffect(() => {
    if (open) {
      logoutInitiatedRef.current = false;
      startTimer();
    }
    return () => {
      if (ticker.current) {
        clearInterval(ticker.current);
        ticker.current = undefined;
      }
    };
  }, [open]);

  useEffect(() => {
    // Only trigger automatic timeout if logout wasn't manually initiated
    // This prevents race condition where duration reaches 0 after manual logout
    if (duration <= 0 && !logoutInitiatedRef.current) {
      if (ticker.current) clearInterval(ticker.current);
      onSessionTimeOut();
    }
  }, [duration, onSessionTimeOut]);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth>
      <DialogTitle>{t('inactiveModal.headingText')}</DialogTitle>
      <DialogContent>
        <Typography variant='bodyLarge'>{`${t('inactiveModal.bodyText1')} ${duration} ${t('inactiveModal.bodyText2')}`}</Typography>
        <Box
          sx={{
            mt: 2,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <Button
            variant='text'
            sx={{
              color: theme.color.primary && `${theme.color.primary} !important`,
              mr: 1,
            }}
            onClick={handleLogout}
          >
            {t('inactiveModal.suffixButton')}
          </Button>
          <Button variant='contained' onClick={handleClose}>
            {t('inactiveModal.prefixButton')}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
export default InactiveLayoutModal;
